// Social Feed TypeScript Types
// Types for the Primary Social Feed system

export interface FeedItem {
  id: string;
  type: FeedItemType;
  userId: string;
  username: string;
  avatar: string;
  timestamp: Date;
  content: FeedItemContent;
  engagement: FeedEngagement;
  metadata: FeedItemMetadata;
}

export type FeedItemType =
  | "heart-games-achievement"
  | "heart-games-completion"
  | "profile-update"
  | "compatibility-suggestion"
  | "social-activity"
  | "community-challenge"
  | "match-announcement"
  | "streak-milestone"
  | "leaderboard-update"
  | "video-content"
  | "image-content"
  | "text-content";

export interface FeedItemContent {
  title?: string;
  description?: string;
  media?: FeedMedia;
  gameType?: string;
  achievement?: AchievementBadge;
  score?: number;
  level?: number;
  streak?: number;
  gameResult?: GameResult;
  compatibilityScore?: number;
  matchReasons?: string[];
  sharedInterests?: string[];
  conversationStarters?: string[];
  personalityAlignment?: PersonalityAlignment;
  challenge?: CommunityChallenge;
  announcement?: MatchAnnouncement;
  // Profile update specific fields
  updateType?: "photos" | "bio" | "interests" | "location";
  newPhotos?: number;
  oldBio?: string;
  newBio?: string;
  // Social activity specific fields
  activityType?: "reading" | "working" | "cooking" | "traveling" | "exercise";
  bookTitle?: string;
  recipe?: string;
  location?: string;
  // Leaderboard specific fields
  leaderboard?: LeaderboardUpdate;
}

export interface LeaderboardUpdate {
  type: "weekly" | "monthly" | "all-time";
  category: "heart-games" | "social" | "matches" | "overall";
  position: number;
  previousPosition?: number;
  points: number;
  change?: string;
}

export interface FeedMedia {
  type: "image" | "video" | "gif" | "audio";
  url: string;
  thumbnail?: string;
  duration?: number;
  aspectRatio?: number;
  alt?: string;
}

export interface FeedEngagement {
  likes: number;
  comments: number;
  shares: number;
  views?: number;
  isLiked: boolean;
  isCommented: boolean;
  isShared: boolean;
}

export interface FeedItemMetadata {
  showSwipeOption?: boolean;
  compatibilityScore?: number;
  isSponsored?: boolean;
  priority?: "low" | "medium" | "high";
  tags?: string[];
  location?: string;
  age?: number;
  distance?: number;
}

export interface FeedFilters {
  contentType?: FeedItemType[];
  location?: string;
  ageRange?: {
    min: number;
    max: number;
  };
  interests?: string[];
  distance?: number;
  showCompatibilityOnly?: boolean;
  showHeartGamesOnly?: boolean;
}

export interface FeedOptions {
  page: number;
  filters: FeedFilters;
  tab: FeedTab;
  limit?: number;
  /** Ordering seed. Changes each time the feed wraps to a new pass. */
  seed?: string;
}

export type FeedTab = "stories" | "heart-games" | "matches" | "explore" | "nearby";

export interface FeedInteraction {
  itemId: string;
  userId: string;
  type: "like" | "comment" | "share" | "view" | "swipe-trigger";
  timestamp: Date;
  metadata?: Record<string, any>;
}

export interface SwipeAction {
  itemId: string;
  userId: string;
  direction: SwipeDirection;
  timestamp: Date;
  compatibilityScore?: number;
}

export type SwipeDirection = "left" | "right" | "up" | "down";

export interface UserPreferences {
  heartGamesInterest: number;
  matchingInterest: number;
  socialInterest: number;
  communityInterest: number;
  competitiveInterest: number;
}

export interface EngagementHistory {
  likedTypes: Record<FeedItemType, number>;
  commentedTypes: Record<FeedItemType, number>;
  sharedTypes: Record<FeedItemType, number>;
  swipeHistory: SwipeAction[];
  timeSpent: Record<FeedItemType, number>;
}

export interface AchievementBadge {
  id: string;
  name: string;
  description: string;
  icon: string;
  rarity: "common" | "rare" | "epic" | "legendary";
  unlockedAt: Date;
  category: "games" | "streaks" | "social" | "milestones";
  gameType?: string;
  level?: number;
}

export interface GameResult {
  gameId: string;
  gameType: string;
  score: number;
  completedAt: Date;
  duration: number;
  answers: any[];
  insights: string[];
}

export interface PersonalityAlignment {
  mbtiCompatibility: number;
  traitSimilarity: number;
  traitComplementarity: number;
  communicationStyle: number;
}

export interface CommunityChallenge {
  id: string;
  title: string;
  description: string;
  type: "daily" | "weekly" | "monthly" | "special";
  startDate: Date;
  endDate: Date;
  participants: number;
  reward: ChallengeReward;
  progress?: number;
  total?: number;
}

export interface ChallengeReward {
  xp: number;
  badges: AchievementBadge[];
  features: FeatureUnlock[];
  social: SocialReward;
}

export interface SocialReward {
  profileHighlight: boolean;
  leaderboardPosition: number;
  exclusiveContent: boolean;
  communityRecognition: boolean;
}

export interface FeatureUnlock {
  id: string;
  name: string;
  description: string;
  requiredXP: number;
  category: "matching" | "profile" | "social" | "premium";
  unlocked: boolean;
  unlockedAt?: Date;
}

export interface MatchAnnouncement {
  id: string;
  userId: string;
  matchedUserId: string;
  matchedAt: Date;
  compatibilityScore: number;
  sharedInterests: string[];
}

// TikTok-like Feed Specific Types
export interface TikTokFeedItem extends FeedItem {
  aspectRatio: number;
  isFullScreen: boolean;
  autoPlay: boolean;
  showControls: boolean;
  mediaType: "video" | "image" | "mixed";
}

export interface FeedViewport {
  width: number;
  height: number;
  currentIndex: number;
  visibleItems: number;
}

export interface FeedScrollState {
  isScrolling: boolean;
  scrollDirection: "up" | "down";
  scrollVelocity: number;
  lastScrollTime: number;
}

// Hook Return Types
export interface UseSocialFeedReturn {
  feedItems: FeedItem[];
  isLoading: boolean;
  hasMore: boolean;
  filters: FeedFilters;
  activeTab: FeedTab;
  setFilters: (filters: FeedFilters) => void;
  setActiveTab: (tab: FeedTab) => void;
  /** `true` restarts the feed from the top; omitted appends the next page. */
  loadFeedItems: (restart?: boolean) => Promise<void>;
  handleInteraction: (interaction: FeedInteraction) => Promise<void>;
  refreshFeed: () => Promise<void>;
}

export interface UseSwipeModeReturn {
  isSwipeModeActive: boolean;
  currentSwipeItem: FeedItem | null;
  swipeHistory: SwipeAction[];
  openSwipeMode: (item: FeedItem) => void;
  closeSwipeMode: () => void;
  handleSwipe: (direction: SwipeDirection) => Promise<void>;
}

export interface UseFeedInteractionsReturn {
  handleLike: (itemId: string) => Promise<void>;
  handleComment: (itemId: string, comment: string) => Promise<void>;
  handleShare: (itemId: string, platform: string) => Promise<void>;
  handleView: (itemId: string) => Promise<void>;
  isInteracting: boolean;
}

// Component Props Types
export interface FeedContainerProps {
  userId: string;
  initialFilters?: FeedFilters;
  onItemInteraction: (interaction: FeedInteraction) => void;
  onSwipeModeToggle: (enabled: boolean) => void;
  className?: string;
}

export interface FeedHeaderProps {
  activeFilters: FeedFilters;
  onFiltersChange: (filters: FeedFilters) => void;
  onSettingsOpen: () => void;
  userLocation?: string;
  swipeModeEnabled: boolean;
  onSwipeModeToggle: () => void;
}

export interface FeedNavigationProps {
  activeTab: FeedTab;
  onTabChange: (tab: FeedTab) => void;
  unreadCounts: Record<FeedTab, number>;
}

export interface BaseFeedItemProps {
  item: FeedItem;
  onInteraction: (interaction: FeedInteraction) => void;
  onUserClick: (userId: string) => void;
  onSwipeModeTrigger?: (item: FeedItem) => void;
  showSwipeOption?: boolean;
  isFullScreen?: boolean;
  aspectRatio?: number;
}

export interface HeartGamesItemProps extends BaseFeedItemProps {
  item: HeartGamesFeedItem;
}

export interface HeartGamesFeedItem extends FeedItem {
  type:
    | "heart-games-achievement"
    | "heart-games-completion"
    | "streak-milestone";
  content: {
    gameType: string;
    achievement?: AchievementBadge;
    score?: number;
    level?: number;
    streak?: number;
    gameResult?: GameResult;
  };
}

export interface CompatibilityItemProps extends BaseFeedItemProps {
  item: CompatibilityFeedItem;
}

export interface CompatibilityFeedItem extends FeedItem {
  type: "compatibility-suggestion";
  content: {
    compatibilityScore: number;
    matchReasons: string[];
    sharedInterests: string[];
    conversationStarters: string[];
    personalityAlignment: PersonalityAlignment;
  };
}

export interface SwipeModeOverlayProps {
  isOpen: boolean;
  item: FeedItem;
  onClose: () => void;
  onSwipe: (direction: SwipeDirection) => void;
  onProfileView: (userId: string) => void;
}

// Service Types
export interface FeedServiceResponse {
  items: FeedItem[];
  hasMore: boolean;
  nextPage: number;
  totalCount: number;
}

export interface FeedAlgorithmWeights {
  recency: number;
  engagement: number;
  compatibility: number;
  userPreferences: number;
  socialProof: number;
  contentQuality: number;
}

// Constants
export const FEED_PAGE_SIZE = 20;
export const FEED_AUTO_REFRESH_INTERVAL = 30000; // 30 seconds
export const SWIPE_THRESHOLD = 50; // pixels
export const SCROLL_VELOCITY_THRESHOLD = 0.5;

export const DEFAULT_FEED_FILTERS: FeedFilters = {
  contentType: [],
  location: undefined,
  ageRange: { min: 18, max: 99 },
  interests: [],
  distance: 50,
  showCompatibilityOnly: false,
  showHeartGamesOnly: false,
};

export const FEED_TAB_ICONS: Record<FeedTab, string> = {
  matches: "💕",  // Moved before stories
  "heart-games": "🎮",
  stories: "📸",  // Moved after matches
  explore: "🔍",
  nearby: "📍",
};

export const FEED_TAB_LABELS: Record<FeedTab, string> = {
  matches: "Matches",  // Moved before moments
  "heart-games": "Heart Games",
  stories: "Moments",  // Moved after matches
  explore: "Explore",
  nearby: "Nearby",
};
